import React, { useState } from "react";
import styles from "./QMSAccordion.module.scss";
import { ReactComponent as CrossIcon } from "../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../icons/check.svg";
import classNames from "classnames";
import axiosInstance from "../../utils/utils";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Button from "../Button/Button";
import { getOrganizationData } from "../../actions/organization";
import { Tooltip } from "react-tooltip";
import Swal from "sweetalert2";
import Spinner from "../Spinner/Spinner";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";

const QMSAccordion = ({ permit, selectedChapter }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { orgId, pageId } = useParams();

  const sessionToken = localStorage.getItem("session_token");

  const [isOpen, setIsOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const module = useSelector((state) => state.user.modules[orgId]);
  const currentModule = module?.find((mod) => mod.path.includes("qms"));
  const spaceKey = currentModule?.params?.space || "";

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  // Check if `selectedChapter` contains a number (A1, A2, ...)
  const isSpecificChapter = selectedChapter && /\d/.test(selectedChapter);

  // Check if ANY question has been validated (qms exists)
  const hasValidation = permit.questions.some((question) => question.qms);

  const validateCompliance = async (e) => {
    e.stopPropagation();

    if (!isSpecificChapter || !permit || !permit.questions.length) return;

    setIsValidating(true); // Show loading state

    // Show a toast notification that this will take time without confirmation
    Swal.fire({
      title: t("qmsAccordion.swal.startTitle"),
      text: t("qmsAccordion.swal.startText"),
      icon: "info",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 7000,
      timerProgressBar: true,
      didOpen: (toast) => {
        const popup = Swal.getPopup();

        popup.addEventListener("mouseenter", () => {
          Swal.stopTimer();
        });

        popup.addEventListener("mouseleave", () => {
          Swal.resumeTimer();
        });
      },
    });

    const chapter = permit.questions[0]?.chapter; // Assume all questions belong to the same chapter
    const questionNums = permit.questions.map((q) => q.num); // Extract 'num' values

    const url = `/ai/qms_permit_questions?org_id=${orgId}&space_key=${spaceKey}&page_id=${pageId}&chapter=${chapter}`;

    try {
      const response = await axiosInstance.post(url, questionNums, {
        headers: {
          "session-token": sessionToken,
        },
      });
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          null,
          false,
          false,
          false,
          false,
          false,
          true,
        ),
      );

      // Show success toast notification
      Swal.fire({
        title: t("qmsAccordion.swal.successTitle"),
        text: t("qmsAccordion.swal.successText"),
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 7000,
        timerProgressBar: true,
        didOpen: (toast) => {
          const popup = Swal.getPopup();

          popup.addEventListener("mouseenter", () => {
            Swal.stopTimer();
          });

          popup.addEventListener("mouseleave", () => {
            Swal.resumeTimer();
          });
        },
      });
    } catch (error) {
      console.error("Error validating compliance:", error);

      // Show error toast notification
      Swal.fire({
        title: t("qmsAccordion.swal.errorTitle"),
        text: t("qmsAccordion.swal.errorText"),
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 5000,
        didOpen: (toast) => {
          const popup = Swal.getPopup();

          popup.addEventListener("mouseenter", () => {
            Swal.stopTimer();
          });

          popup.addEventListener("mouseleave", () => {
            Swal.resumeTimer();
          });
        },
      });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className={styles.accordion}>
      <div
        className={classNames(styles.accordionButton, isOpen && styles.opened)}
        onClick={toggleAccordion}
      >
        {permit.name} {/* Permit name at the top */}
        <div className={styles.buttonGroup}>
          {isSpecificChapter && (
            <Button
              text={isValidating ? t("qmsAccordion.validating") : t("qmsAccordion.validate")}
              onClick={validateCompliance}
              disabled={isValidating}
              className={styles.validateButton}
              icon={isValidating ? <Spinner /> : null}
            />
          )}
          {isOpen ? (
            <X aria-hidden="true" className={styles.toggleIcon} size={18} />
          ) : (
            <Plus aria-hidden="true" className={styles.toggleIcon} size={18} />
          )}
        </div>
      </div>
      {isOpen && (
        <div className={styles.accordionContent}>
          <div
            className={classNames(
              styles.grid,
              hasValidation ? styles.withValidation : styles.noValidation,
            )}
          >
            <div className={styles.gridHeader}>
              <div className={styles.gridHeadCell}>
                {t("qmsAccordion.table.chapter")}
              </div>
              <div className={classNames(styles.gridHeadCell, styles.center)}>
                #
              </div>
              <div className={styles.gridHeadCell}>
                {t("qmsAccordion.table.question")}
              </div>
              {hasValidation && (
                <div className={classNames(styles.gridHeadCell, styles.center)}>
                  {t("qmsAccordion.table.validation")}
                </div>
              )}
            </div>

            <div className={styles.gridBody}>
              {permit.questions.map((question, index) => {
                const uniqueKey =
                  question.uuid ||
                  `${question.chapter}-${question.num}` ||
                  `question-${index}`;

                return (
                  <div key={uniqueKey} className={styles.gridRow}>
                    <div className={classNames(styles.gridCell, styles.center)}>
                      {question.chapter}
                    </div>
                    <div className={classNames(styles.gridCell, styles.center)}>
                      {question.num}
                    </div>
                    <div className={classNames(styles.gridCell, styles.questionCell)}>
                      {question.question}
                    </div>

                    {hasValidation && (
                      <div className={classNames(styles.gridCell, styles.center)}>
                        {question.qms ? (
                          <>
                            <span
                              data-tooltip-id={`tooltip-${question.num}`}
                              data-tooltip-content={question.qms.explanation}
                            >
                              {question.qms.answered ? (
                                <CheckIcon
                                  className={classNames(
                                    styles.validationIcon,
                                    styles.check,
                                  )}
                                />
                              ) : (
                                <CrossIcon
                                  className={classNames(
                                    styles.validationIcon,
                                    styles.cross,
                                  )}
                                />
                              )}
                            </span>
                            <Tooltip
                              id={`tooltip-${question.num}`}
                              place="top"
                              className={styles.tooltip}
                            />
                          </>
                        ) : (
                          <span className={styles.noData}>
                            {t("qmsAccordion.table.noValidation")}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QMSAccordion;
